/**
 * Inline expanding header search (desktop).
 * Clicking the magnifying glass expands an input field inline (no drawer),
 * so third-party search apps (e.g. Searchanise) can attach their overlay
 * directly to the field.
 */
if (!customElements.get('header-inline-search')) {
  customElements.define(
    'header-inline-search',
    class HeaderInlineSearch extends HTMLElement {
      connectedCallback() {
        this.form = this.querySelector('form');
        this.input = this.querySelector('input[type="search"]');
        this.toggle = this.querySelector('[data-search-toggle]');

        if (!this.form || !this.input || !this.toggle) return;

        this._onToggleClick = this._onToggleClick.bind(this);
        this._onKeydown = this._onKeydown.bind(this);
        this._onDocumentPointerDown = this._onDocumentPointerDown.bind(this);

        this.toggle.addEventListener('click', this._onToggleClick);
        this.input.addEventListener('keydown', this._onKeydown);
        document.addEventListener('pointerdown', this._onDocumentPointerDown);
      }

      disconnectedCallback() {
        document.removeEventListener('pointerdown', this._onDocumentPointerDown);
      }

      get isExpanded() {
        return this.hasAttribute('expanded');
      }

      get isTriggerOnly() {
        return this.hasAttribute('trigger-only');
      }

      _onToggleClick(event) {
        event.preventDefault();

        // Trigger-only mode (e.g. mobile): just hand off to the third-party
        // search app by focusing the field, no inline expansion / theme drawer.
        if (this.isTriggerOnly) {
          this.openExternalSearch();
          return;
        }

        if (!this.isExpanded) {
          this.expand();
          return;
        }

        if (this.input.value.trim() !== '') {
          this.form.requestSubmit ? this.form.requestSubmit() : this.form.submit();
          return;
        }

        this.collapse();
      }

      _onKeydown(event) {
        if (event.key === 'Escape') {
          this.collapse();
          this.toggle.focus();
        }
      }

      _onDocumentPointerDown(event) {
        if (!this.isExpanded) return;
        // Keep open while interacting with the field or the search app overlay.
        if (this.contains(event.target)) return;
        if (this.input.value.trim() !== '') return;
        this.collapse();
      }

      openExternalSearch() {
        this.input.removeAttribute('tabindex');
        this.input.focus();
        // Some search apps bind their open handler to click rather than focus.
        this.input.click();
      }

      expand() {
        this.setAttribute('expanded', '');
        this.toggle.setAttribute('aria-expanded', 'true');
        this.input.removeAttribute('tabindex');
        this.input.focus();
      }

      collapse() {
        this.removeAttribute('expanded');
        this.toggle.setAttribute('aria-expanded', 'false');
        this.input.setAttribute('tabindex', '-1');
        this.input.blur();
      }
    }
  );
}
